import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { PublicLayout } from "@/components/PublicLayout";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, ArrowRight, Home } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

type PaymentState = "polling" | "success" | "pending" | "failed" | "timeout";

export default function PaymentCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState<PaymentState>("polling");
  const [tier, setTier] = useState<string | null>(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("failed");
      return;
    }

    let active = true;
    const maxAttempts = 10; // 10 × 2s = 20s max
    const poll = async (tries: number) => {
      if (!active) return;
      if (tries >= maxAttempts) {
        setState("timeout");
        return;
      }
      try {
        const res = await fetch(`${API_URL}/api/subscriptions/checkout/status/${sessionId}`, {
          credentials: "include" as RequestCredentials,
        });
        if (!res.ok) {
          setState("failed");
          return;
        }
        const data = await res.json();
        setAttempts(tries + 1);
        if (data.payment_status === "paid") {
          setTier(data.tier);
          setState("success");
          return;
        }
        if (data.status === "expired") {
          setState("failed");
          return;
        }
        setState("pending");
        setTimeout(() => poll(tries + 1), 2000);
      } catch {
        setState("failed");
      }
    };
    poll(0);

    return () => {
      active = false;
    };
  }, [location.search]);

  return (
    <PublicLayout>
      <section className="min-h-[70vh] flex items-center justify-center py-20">
        <div className="max-w-lg w-full px-4 text-center" data-testid="payment-callback">
          {state === "polling" || state === "pending" ? (
            <div className="space-y-5">
              <div className="h-16 w-16 rounded-2xl bg-primary/10 mx-auto flex items-center justify-center">
                <Loader2 className="h-8 w-8 text-primary animate-spin" />
              </div>
              <h1 className="text-3xl font-bold">Vérification du paiement…</h1>
              <p className="text-muted-foreground">
                Nous confirmons votre abonnement auprès de Stripe. Ne fermez pas cette page.
              </p>
              <p className="text-xs text-muted-foreground/60">Tentative {attempts} / 10</p>
            </div>
          ) : state === "success" ? (
            <div className="space-y-5" data-testid="payment-success">
              <div className="h-16 w-16 rounded-2xl bg-emerald-500/10 mx-auto flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8 text-emerald-500" />
              </div>
              <h1 className="text-3xl font-bold">Paiement confirmé 🎉</h1>
              <p className="text-muted-foreground">
                Votre abonnement <span className="font-semibold text-foreground">{tier || ""}</span> est actif.
                Bienvenue dans la nouvelle ère de la gestion d'entreprise.
              </p>
              <div className="flex gap-3 justify-center pt-2">
                <Button variant="hero" onClick={() => navigate("/dashboard")} data-testid="payment-goto-dashboard">
                  Accéder à mon dashboard <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Link to="/usage">
                  <Button variant="outline">Voir ma consommation</Button>
                </Link>
              </div>
            </div>
          ) : state === "timeout" ? (
            <div className="space-y-5">
              <div className="h-16 w-16 rounded-2xl bg-amber-500/10 mx-auto flex items-center justify-center">
                <Loader2 className="h-8 w-8 text-amber-500" />
              </div>
              <h1 className="text-3xl font-bold">Vérification prolongée</h1>
              <p className="text-muted-foreground">
                Stripe prend un peu plus de temps que d'habitude. Votre paiement sera confirmé automatiquement.
                Vous pouvez rafraîchir cette page dans quelques secondes.
              </p>
              <Button variant="hero" onClick={() => window.location.reload()}>
                Rafraîchir la page
              </Button>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="h-16 w-16 rounded-2xl bg-destructive/10 mx-auto flex items-center justify-center">
                <XCircle className="h-8 w-8 text-destructive" />
              </div>
              <h1 className="text-3xl font-bold">Paiement non abouti</h1>
              <p className="text-muted-foreground">
                Nous n'avons pas pu confirmer votre paiement. Aucun montant n'a été prélevé.
              </p>
              <div className="flex gap-3 justify-center pt-2">
                <Link to="/tarifs">
                  <Button variant="hero">Réessayer</Button>
                </Link>
                <Link to="/">
                  <Button variant="outline"><Home className="mr-2 h-4 w-4" /> Accueil</Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
